import React from 'react';
import {
    Grid,
    Typography,
    TextField,
    Button,
    Dialog,
    DialogContent,
    MenuItem,
} from '@mui/material';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import AppDialogTitle from '../../../../ui/appDialogTitle';
import { isLocalDev } from '../../../../../utils/environment';
import { ContactLensConfigurationService } from '../../../../../service/validDataService';
import validLookup from '../../../../../utils/validLookup';

export const initialValuesMaterialForm = {
    name: '',
    waterContent: '',
    oxygenPermeability: '',
    status: validLookup.validStatus.active,
    notes: '',
};

const validationSchema = Yup.object({
    name: Yup.string().required('Material name is required'),
    waterContent: Yup.number()
        .nullable()
        .positive('Water content must be positive')
        .max(100, 'Water content cannot exceed 100%'),
    oxygenPermeability: Yup.number()
        .nullable()
        .positive('Oxygen permeability must be positive'),
});

export default function ContactLensMaterialInputForm({ open, onClose, onRecordAdded }) {

    const handleSubmit = async (values, { resetForm, setSubmitting }) => {
        try {
            const response = await ContactLensConfigurationService.addMaterial(values);
            const { code, data, message } = response;

            setSubmitting(false);

            if (code === 0) {
                toast.success(message);
                resetForm();
                onRecordAdded(data);
                onClose();
            } else {
                toast.error(message);
            }
        } catch (error) {
            if (isLocalDev) {
                console.error(error);
            }
            setSubmitting(false);
            toast.error('Failed to add contact lens material');
        }
    };

    return (
        <Dialog open={open} maxWidth={'sm'} fullWidth>
            <AppDialogTitle title={"Add Contact Lens Material"} onClose={onClose} />
            <DialogContent sx={{ mt: 1, mb: 1 }}>
                <Formik
                    initialValues={initialValuesMaterialForm}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ isSubmitting }) => (
                        <Form>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="name"
                                        label="Material Name"
                                        required
                                        fullWidth
                                        size="small"
                                        helperText="e.g., Senofilcon A, Comfilcon A, Delefilcon A"
                                    />
                                    <ErrorMessage name="name">{message => <Typography color={'error'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={6}>
                                    <Field
                                        as={TextField}
                                        name="waterContent"
                                        label="Water Content (%)"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        inputProps={{ step: 0.01, min: 0, max: 100 }}
                                        helperText="Percentage (0-100)"
                                    />
                                    <ErrorMessage name="waterContent">{message => <Typography color={'error'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={6}>
                                    <Field
                                        as={TextField}
                                        name="oxygenPermeability"
                                        label="Oxygen Permeability (Dk)"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        inputProps={{ step: 1, min: 0 }}
                                        helperText="Dk value"
                                    />
                                    <ErrorMessage name="oxygenPermeability">{message => <Typography color={'error'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name='status'
                                        label="Status"
                                        fullWidth
                                        select
                                        size="small"
                                    >
                                        <MenuItem value={"active"}>Active</MenuItem>
                                        <MenuItem value={"inactive"}>Inactive</MenuItem>
                                    </Field>
                                </Grid>
                                <Grid item xs={12}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Adding...' : 'Add Material'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>
            </DialogContent>
        </Dialog>
    );
}
